import { ACCESS_GRANTED_PARAM } from '@pypestream/utils';

/**
 * Fixed wait between a successful role assignment and the redirect. The role
 * reaches the permission layer through Kafka; there is no completion signal and
 * nothing to poll, so this timer IS the contract.
 */
export const ACCESS_SYNC_WAIT_MS = 5000;

export type AccessSetupStatus = 'loading' | 'granted' | 'failed' | 'denied';

/** Backend `extensions.code` values that mean "this user cannot get access". */
const DENIED_CODES: ReadonlySet<string> = new Set([
  'UNAUTHORIZED', // no right to grant the role
  'UNPROCESSABLE', // project not found
]);

/**
 * Classify a failed `assignProjectProductAdminRoles` call. A denial is final
 * (retrying cannot help); anything else - network, 5xx, an unknown or missing
 * code, a non-error value - is `failed` and offers Retry.
 */
export const classifyAssignError = (error: unknown): 'denied' | 'failed' => {
  const graphQLErrors = (
    error as { graphQLErrors?: unknown } | null | undefined
  )?.graphQLErrors;
  if (!Array.isArray(graphQLErrors)) {
    return 'failed';
  }
  const denied = graphQLErrors.some((e) => {
    const code = (e as { extensions?: { code?: unknown } } | null)?.extensions
      ?.code;
    return typeof code === 'string' && DENIED_CODES.has(code);
  });
  return denied ? 'denied' : 'failed';
};

// The access-granted signal's param NAME is the cross-app wire contract; it lives
// in @pypestream/utils so a rename cannot silently desync Manager and Pro Studio.
// Re-exported so this app's utils barrel and the tests keep reaching it here.
export { ACCESS_GRANTED_PARAM };

/**
 * Add the access-granted flag to a redirect target, keeping a same-origin
 * relative target relative and an absolute URL absolute. A target that cannot be
 * parsed is returned unchanged (the redirect still happens, just without the
 * toast signal).
 */
export const withAccessGrantedFlag = (
  target: string,
  currentOrigin: string
): string => {
  const isAbsolute = /^https?:\/\//i.test(target);
  let url: URL;
  try {
    url = new URL(target, currentOrigin);
  } catch {
    return target;
  }
  url.searchParams.set(ACCESS_GRANTED_PARAM, '1');
  if (isAbsolute) {
    return url.toString();
  }
  // Keep a relative target relative — the redirect used navigate() for these.
  return `${url.pathname}${url.search}${url.hash}`;
};

/** Whether the current location's search carries the access-granted flag. */
export const hasAccessGrantedFlag = (search: string): boolean =>
  new URLSearchParams(search).get(ACCESS_GRANTED_PARAM) === '1';

/**
 * Remove the access-granted flag from a search string, preserving every other
 * param in order AND its encoding. Works on the raw pairs rather than
 * `URLSearchParams.toString()`, which would re-encode the remaining params
 * (`%20`→`+`) and so rewrite a URL the user never touched. Returns a search
 * string including the leading `?`, or `''`. (Same shape as Pro Studio's
 * `access-granted.ts` twin — the two strips behave identically.)
 */
export const stripAccessGrantedParam = (search: string): string => {
  const kept = search
    .replace(/^\?/, '')
    .split('&')
    .filter(Boolean)
    .filter((pair) => pair.split('=')[0] !== ACCESS_GRANTED_PARAM);

  return kept.length ? `?${kept.join('&')}` : '';
};
